// pinia plugin that will persist values in indexdb storage
import { del, get, set, createStore } from "idb-keyval";
import type { PiniaPluginContext } from "pinia";
import { ref } from "vue";
import "pinia";

declare module "pinia" {
  export interface DefineStoreOptionsBase<S, Store> {
    indexdbStorage?: Partial<Record<keyof StoreActions<Store>, unknown>> & {
      toStorage?: (state: S) => S;
    };
  }
  export interface PiniaCustomStateProperties {
    indexdbStoragePluginLoadedPromise: Promise<void>;
  }
}

/**
 * Note - you must await indexdbStoragePluginLoadedPromise; before you can be sure that the state has been loaded from indexdb storage,
 * so in usage this looks like
 *    const payrollStore = usePayrollStore();
 *    await payrollStore.indexdbStoragePluginLoadedPromise;
 */
export default function indexdbStoragePlugin({
  options,
  store,
}: PiniaPluginContext) {
  if (options.indexdbStorage != null) {
    const customStore = createStore("pinia", "keyval-store");
    const key = store.$id;
    const indexdbStoragePluginLoaded = ref(false);
    const indexdbStoragePluginLoadedPromise = get(key, customStore);
    indexdbStoragePluginLoadedPromise
      .then((state) => {
        if (state != null) {
          store.$patch(state);
        }
      })
      .finally(() => {
        indexdbStoragePluginLoaded.value = true;
      });

    // don't subscribe to changes until the initial state has been loaded
    store.$subscribe((mutation, state) => {
      let saveState = state;
      if (indexdbStoragePluginLoaded.value === false) {
        // if the plugin hasn't loaded yet, don't save the state
        return;
      }
      if (options.indexdbStorage?.toStorage) {
        saveState = options.indexdbStorage.toStorage(state);
      }
      set(key, saveState, customStore);
    });

    // override the $reset function
    const originalReset = store.$reset.bind(store);
    return {
      $reset() {
        originalReset();
        del(key, customStore);
      },
      indexdbStoragePluginLoaded,
      indexdbStoragePluginLoadedPromise,
    };
  }
}
